/**
 * UK and international banking field normalization, formatting, and validation.
 * Used by form schemas and payment row validation for consistent behaviour.
 */

export const SORT_CODE_DIGIT_LENGTH = 6;
export const UK_ACCOUNT_NUMBER_MIN_LENGTH = 6;
export const UK_ACCOUNT_NUMBER_MAX_LENGTH = 8;
export const IBAN_MIN_LENGTH = 15;
export const IBAN_MAX_LENGTH = 34;
export const BIC_SWIFT_LENGTH_SHORT = 8;
export const BIC_SWIFT_LENGTH_LONG = 11;

export const BANKING_FIELD_PLACEHOLDERS = {
  sortCode: '12-34-56',
  accountNumber: '12345678',
  iban: 'GB82 WEST 1234 5698 7654 32',
  bicSwift: 'ABCDGB2L',
} as const;

const SORT_CODE_PATTERN = /^\d{2}-\d{2}-\d{2}$/;
const UK_ACCOUNT_NUMBER_PATTERN = /^\d{6,8}$/;
const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]+$/;
const BIC_SWIFT_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

const stripNonDigits = (value: unknown): string =>
  String(value ?? '').replace(/\D/g, '');

const stripToAlphanumeric = (value: unknown): string =>
  String(value ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();

export const normalizeSortCode = (value: unknown): string =>
  stripNonDigits(value).slice(0, SORT_CODE_DIGIT_LENGTH);

export const normalizeAccountNumber = (value: unknown): string =>
  stripNonDigits(value).slice(0, UK_ACCOUNT_NUMBER_MAX_LENGTH);

export const normalizeIban = (value: unknown): string =>
  stripToAlphanumeric(value).slice(0, IBAN_MAX_LENGTH);

export const normalizeBicSwift = (value: unknown): string =>
  stripToAlphanumeric(value).slice(0, BIC_SWIFT_LENGTH_LONG);

export const formatSortCode = (value: unknown): string => {
  const digits = normalizeSortCode(value);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

export const formatIban = (value: unknown): string => {
  const normalized = normalizeIban(value);
  return normalized.replace(/(.{4})(?=.)/g, '$1 ').trim();
};

export const formatBicSwift = (value: unknown): string =>
  normalizeBicSwift(value);

const hasValidIbanChecksum = (iban: string): boolean => {
  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
  let remainder = '';

  for (const char of rearranged) {
    remainder += /[0-9]/.test(char)
      ? char
      : String(char.charCodeAt(0) - 55);

    if (remainder.length > 9) {
      remainder = String(Number(remainder.slice(0, -9)) % 97) + remainder.slice(-9);
    }
  }

  return Number(remainder) % 97 === 1;
};

export const isValidSortCode = (value: unknown): boolean => {
  const formatted = formatSortCode(value);
  if (!SORT_CODE_PATTERN.test(formatted)) {
    return false;
  }

  return formatted !== '00-00-00';
};

export const isValidAccountNumber = (value: unknown): boolean =>
  UK_ACCOUNT_NUMBER_PATTERN.test(normalizeAccountNumber(value));

export const isValidIban = (value: unknown): boolean => {
  const normalized = normalizeIban(value);

  if (
    normalized.length < IBAN_MIN_LENGTH ||
    normalized.length > IBAN_MAX_LENGTH
  ) {
    return false;
  }

  if (!IBAN_PATTERN.test(normalized)) {
    return false;
  }

  return hasValidIbanChecksum(normalized);
};

export const isValidBicSwift = (value: unknown): boolean => {
  const normalized = normalizeBicSwift(value);
  return (
    normalized.length === BIC_SWIFT_LENGTH_SHORT ||
    normalized.length === BIC_SWIFT_LENGTH_LONG
  ) && BIC_SWIFT_PATTERN.test(normalized);
};

export const validateSortCode = (
  value: unknown,
  fieldName: string = 'Sort code'
): string | undefined => {
  if (!value || String(value).trim() === '') {
    return `${fieldName} is required`;
  }

  const digits = normalizeSortCode(value);
  if (digits.length < SORT_CODE_DIGIT_LENGTH) {
    return `${fieldName} must be ${SORT_CODE_DIGIT_LENGTH} digits (e.g. 12-34-56)`;
  }

  if (!isValidSortCode(value)) {
    return `${fieldName} must be a valid 6-digit sort code (e.g. 12-34-56)`;
  }

  return undefined;
};

export const validateAccountNumber = (
  value: unknown,
  fieldName: string = 'Account number'
): string | undefined => {
  if (!value || String(value).trim() === '') {
    return `${fieldName} is required`;
  }

  const digits = normalizeAccountNumber(value);
  if (digits.length < UK_ACCOUNT_NUMBER_MIN_LENGTH) {
    return `${fieldName} must be ${UK_ACCOUNT_NUMBER_MIN_LENGTH}-${UK_ACCOUNT_NUMBER_MAX_LENGTH} digits`;
  }

  if (!isValidAccountNumber(value)) {
    return `${fieldName} must contain only digits (${UK_ACCOUNT_NUMBER_MIN_LENGTH}-${UK_ACCOUNT_NUMBER_MAX_LENGTH} digits)`;
  }

  return undefined;
};

export const validateIban = (
  value: unknown,
  fieldName: string = 'IBAN'
): string | undefined => {
  if (!value || String(value).trim() === '') {
    return `${fieldName} is required`;
  }

  const normalized = normalizeIban(value);
  if (normalized.length < IBAN_MIN_LENGTH) {
    return `${fieldName} must be at least ${IBAN_MIN_LENGTH} characters`;
  }

  if (normalized.length > IBAN_MAX_LENGTH) {
    return `${fieldName} must be no more than ${IBAN_MAX_LENGTH} characters`;
  }

  if (!IBAN_PATTERN.test(normalized)) {
    return `${fieldName} must start with a 2-letter country code followed by 2 check digits`;
  }

  if (!hasValidIbanChecksum(normalized)) {
    return `${fieldName} is not a valid IBAN (checksum failed)`;
  }

  return undefined;
};

export const validateBicSwift = (
  value: unknown,
  fieldName: string = 'BIC/SWIFT code'
): string | undefined => {
  if (!value || String(value).trim() === '') {
    return `${fieldName} is required`;
  }

  const normalized = normalizeBicSwift(value);
  if (normalized.length < BIC_SWIFT_LENGTH_SHORT) {
    return `${fieldName} must be at least ${BIC_SWIFT_LENGTH_SHORT} characters`;
  }

  if (!isValidBicSwift(value)) {
    return `${fieldName} must be 8 or 11 characters (e.g. ABCDGB2L or ABCDGB2LXXX)`;
  }

  return undefined;
};

export const validateInternationalBanking = (
  iban: unknown,
  swiftBicCode: unknown,
  bankName: unknown
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const ibanError = validateIban(iban, 'IBAN');
  if (ibanError) {
    errors.iban = ibanError;
  }

  const swiftError = validateBicSwift(swiftBicCode, 'SWIFT/BIC code');
  if (swiftError) {
    errors.swiftBicCode = swiftError;
  }

  if (!bankName || String(bankName).trim().length < 2) {
    errors.bankName = 'Bank name is required for international payments';
  }

  return errors;
};

const BANKING_FIELD_NORMALIZERS: Record<
  string,
  (value: unknown) => string
> = {
  sortCode: formatSortCode,
  accountNo: normalizeAccountNumber,
  accno: normalizeAccountNumber,
  iban: formatIban,
  bic: formatBicSwift,
  swiftBicCode: formatBicSwift,
};

/**
 * Normalizes known banking fields for storage/submission.
 */
export const normalizeBankingFormData = <T extends Record<string, unknown>>(
  data: T
): T => {
  const normalizedEntries = Object.entries(data).map(([key, value]) => {
    const normalizer = BANKING_FIELD_NORMALIZERS[key];
    if (!normalizer || value === undefined || value === null || value === '') {
      return [key, value];
    }

    return [key, normalizer(value)];
  });

  return Object.fromEntries(normalizedEntries) as T;
};
